import React, { createContext, useContext } from "react";
import {
  fetchUserPreferences,
  getCurrentUserPreferenceKey,
  saveUserPreference,
} from "../utils/userPreferences";

export type DashboardCardId =
  | "risk_objects_overview"
  | "contracts_overview"
  | "contacts_overview"
  | "damages_overview"
  | "todays_appointments"
  | "team_appointments"
  | "contracts_by_status"
  | "damages_by_branch"
  | "risk_kfz_overview"
  | "risk_buildings_overview"
  | "open_tasks"
  | "twv_board"
  | "team_twv_board"
  | "meine_vorgaenge"
  | "team_vorgaenge"
  | "meine_todos"
  | "team_todos";

export type DashboardCardSpan = 1 | 2 | 3 | 4;

type DashboardCardDefinition = {
  id: DashboardCardId;
  title: string;
  description: string;
};

type AppSettingsContextValue = {
  visibleDashboardCardIds: DashboardCardId[];
  dashboardCardSpans: Record<DashboardCardId, DashboardCardSpan>;
  setDashboardCardVisibility: (cardId: DashboardCardId, visible: boolean) => void;
  setDashboardCardSpan: (cardId: DashboardCardId, span: DashboardCardSpan) => void;
  moveDashboardCard: (activeCardId: DashboardCardId, overCardId: DashboardCardId) => void;
  resetDashboardCards: () => void;
};

const DASHBOARD_CARDS_STORAGE_KEY = "app-settings.dashboard.visible-cards";
const DASHBOARD_CARD_SPANS_STORAGE_KEY = "app-settings.dashboard.card-spans";
const DASHBOARD_VISIBLE_CARDS_SCOPE = "dashboard.visible-cards";
const DASHBOARD_CARD_SPANS_SCOPE = "dashboard.card-spans";

export const dashboardCardDefinitions: DashboardCardDefinition[] = [
  {
    id: "risk_objects_overview",
    title: "Risikoobjekte",
    description: "Gesamtanzahl aller Risikoobjekte.",
  },
  {
    id: "contracts_overview",
    title: "Verträge",
    description: "Vertragsübersicht und Navigation.",
  },
  {
    id: "contacts_overview",
    title: "Kontakte",
    description: "Kontaktzahlen mit Kunden- und Interessentenanteil.",
  },
  {
    id: "todays_appointments",
    title: "Meine Termine",
    description: "Persönliche Agenda aus dem Microsoft-Kalender.",
  },
  {
    id: "team_appointments",
    title: "Team-Termine",
    description: "Anstehende Termine des Teams (Mock).",
  },
  {
    id: "damages_overview",
    title: "Schäden",
    description: "Übersicht und Schnellaktionen.",
  },
  {
    id: "risk_kfz_overview",
    title: "Risiko KFZ",
    description: "Anzahl der Risikoobjekte in Kategorie KFZ.",
  },
  {
    id: "risk_buildings_overview",
    title: "Risiko Gebäude",
    description: "Anzahl der Risikoobjekte in Kategorie Immobilien.",
  },
  {
    id: "contracts_by_status",
    title: "Verträge nach Status",
    description: "Statusverteilung von Verträgen.",
  },
  {
    id: "damages_by_branch",
    title: "Schäden nach Zweig",
    description: "Verteilung nach Sparte.",
  },
  {
    id: "open_tasks",
    title: "Offene Aufgaben",
    description: "Aufgabenliste für die nächsten sechs Tage.",
  },
  {
    id: "twv_board",
    title: "Meine TWVs",
    description: "Persönliche Wiedervorlagen für heute und morgen.",
  },
  {
    id: "team_twv_board",
    title: "Team TWVs",
    description: "Wiedervorlagen des Teams für heute und morgen.",
  },
  {
    id: "meine_vorgaenge",
    title: "Meine Vorgänge",
    description: "Vorgänge, an denen ich aktiv beteiligt bin.",
  },
  {
    id: "team_vorgaenge",
    title: "Team Vorgänge",
    description: "Laufende Vorgänge des Teams.",
  },
  {
    id: "meine_todos",
    title: "Meine Vorgänge",
    description: "Offene und in Bearbeitung befindliche Todos für mich.",
  },
  {
    id: "team_todos",
    title: "Team Vorgänge",
    description: "Offene und in Bearbeitung befindliche Todos des Teams.",
  },
];

const defaultVisibleDashboardCardIds = dashboardCardDefinitions.map((card) => card.id);
const defaultDashboardCardSpans: Record<DashboardCardId, DashboardCardSpan> = {
  risk_objects_overview: 1,
  contracts_overview: 1,
  contacts_overview: 1,
  damages_overview: 1,
  risk_kfz_overview: 1,
  risk_buildings_overview: 1,
  todays_appointments: 1,
  team_appointments: 1,
  contracts_by_status: 1,
  damages_by_branch: 1,
  open_tasks: 3,
  twv_board: 1,
  team_twv_board: 1,
  meine_vorgaenge: 2,
  team_vorgaenge: 2,
  meine_todos: 2,
  team_todos: 2,
};

const AppSettingsContext = createContext<AppSettingsContextValue | undefined>(undefined);

const isDashboardCardId = (value: unknown): value is DashboardCardId =>
  typeof value === "string" &&
  dashboardCardDefinitions.some((card) => card.id === value);

const isDashboardCardIdArray = (value: unknown): value is DashboardCardId[] =>
  Array.isArray(value) && value.every(isDashboardCardId);

const sanitizeVisibleDashboardCardIds = (value: unknown): DashboardCardId[] => {
  if (!Array.isArray(value)) return defaultVisibleDashboardCardIds;

  const ids = value.filter(isDashboardCardId);
  const ordered = defaultVisibleDashboardCardIds.filter((id) => ids.includes(id));
  return value.length === 0 || ordered.length > 0 ? ordered : defaultVisibleDashboardCardIds;
};

const sanitizeDashboardCardSpans = (
  value: unknown,
): Record<DashboardCardId, DashboardCardSpan> => {
  if (!value || typeof value !== "object") return defaultDashboardCardSpans;

  const next = { ...defaultDashboardCardSpans };
  defaultVisibleDashboardCardIds.forEach((cardId) => {
    const span = (value as Record<string, unknown>)[cardId];
    if (span === 1 || span === 2 || span === 3 || span === 4) {
      next[cardId] = span;
    }
  });

  return next;
};

const getDashboardStorageKey = (baseKey: string, userKey: string | null) =>
  userKey ? `${baseKey}:${userKey}` : baseKey;

const readVisibleDashboardCardIds = (): DashboardCardId[] => {
  if (typeof window === "undefined") return defaultVisibleDashboardCardIds;

  try {
    const raw = window.localStorage.getItem(DASHBOARD_CARDS_STORAGE_KEY);
    return sanitizeVisibleDashboardCardIds(raw ? JSON.parse(raw) : null);
  } catch {
    return defaultVisibleDashboardCardIds;
  }
};

const readDashboardCardSpans = (): Record<DashboardCardId, DashboardCardSpan> => {
  if (typeof window === "undefined") return defaultDashboardCardSpans;

  try {
    const raw = window.localStorage.getItem(DASHBOARD_CARD_SPANS_STORAGE_KEY);
    return sanitizeDashboardCardSpans(raw ? JSON.parse(raw) : null);
  } catch {
    return defaultDashboardCardSpans;
  }
};

export const clearAppSettingsSession = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(DASHBOARD_CARDS_STORAGE_KEY);
  window.localStorage.removeItem(DASHBOARD_CARD_SPANS_STORAGE_KEY);
};

export const AppSettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [visibleDashboardCardIds, setVisibleDashboardCardIds] = React.useState<DashboardCardId[]>(
    () => readVisibleDashboardCardIds(),
  );
  const [dashboardCardSpans, setDashboardCardSpans] = React.useState<
    Record<DashboardCardId, DashboardCardSpan>
  >(() => readDashboardCardSpans());
  const [userPreferenceKey, setUserPreferenceKey] = React.useState<string | null>(null);
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    const hydrateSettings = async () => {
      const resolvedUserKey = await getCurrentUserPreferenceKey();
      if (!mounted) return;

      setUserPreferenceKey(resolvedUserKey);

      const cardsStorageKey = getDashboardStorageKey(
        DASHBOARD_CARDS_STORAGE_KEY,
        resolvedUserKey,
      );
      const spansStorageKey = getDashboardStorageKey(
        DASHBOARD_CARD_SPANS_STORAGE_KEY,
        resolvedUserKey,
      );

      const localCards = () => {
        if (typeof window === "undefined") return defaultVisibleDashboardCardIds;
        try {
          const raw = window.localStorage.getItem(cardsStorageKey);
          return sanitizeVisibleDashboardCardIds(raw ? JSON.parse(raw) : null);
        } catch {
          return defaultVisibleDashboardCardIds;
        }
      };

      const localSpans = () => {
        if (typeof window === "undefined") return defaultDashboardCardSpans;
        try {
          const raw = window.localStorage.getItem(spansStorageKey);
          return sanitizeDashboardCardSpans(raw ? JSON.parse(raw) : null);
        } catch {
          return defaultDashboardCardSpans;
        }
      };

      try {
        if (resolvedUserKey) {
          const prefs = await fetchUserPreferences([
            DASHBOARD_VISIBLE_CARDS_SCOPE,
            DASHBOARD_CARD_SPANS_SCOPE,
          ]);
          const serverCards = prefs[DASHBOARD_VISIBLE_CARDS_SCOPE] as DashboardCardId[] | undefined;
          const serverSpans = prefs[DASHBOARD_CARD_SPANS_SCOPE] as
            | Record<DashboardCardId, DashboardCardSpan>
            | undefined;
          if (!mounted) return;

          const nextCards = isDashboardCardIdArray(serverCards)
            ? sanitizeVisibleDashboardCardIds(serverCards)
            : localCards();
          const nextSpans = serverSpans
            ? sanitizeDashboardCardSpans(serverSpans)
            : localSpans();

          setVisibleDashboardCardIds(nextCards);
          setDashboardCardSpans(nextSpans);

          if (typeof window !== "undefined") {
            window.localStorage.setItem(cardsStorageKey, JSON.stringify(nextCards));
            window.localStorage.setItem(spansStorageKey, JSON.stringify(nextSpans));
          }
          setIsHydrated(true);
          return;
        }
      } catch (error) {
        console.error("Failed to load dashboard user preferences", error);
      }

      if (!mounted) return;
      setVisibleDashboardCardIds(localCards());
      setDashboardCardSpans(localSpans());
      setIsHydrated(true);
    };

    hydrateSettings();

    return () => {
      mounted = false;
    };
  }, []);

  // Ersten Lauf nach der Hydration NICHT zurückschreiben — nur den Ausgangsstand
  // merken (verhindert den redundanten PUT beim Laden); danach nur bei echter
  // Änderung speichern.
  const savedCardsSnapshotRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return;
    const snapshot = JSON.stringify(visibleDashboardCardIds);
    if (savedCardsSnapshotRef.current === null) {
      savedCardsSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === savedCardsSnapshotRef.current) return;
    savedCardsSnapshotRef.current = snapshot;

    const storageKey = getDashboardStorageKey(
      DASHBOARD_CARDS_STORAGE_KEY,
      userPreferenceKey,
    );
    window.localStorage.setItem(storageKey, JSON.stringify(visibleDashboardCardIds));

    if (!userPreferenceKey) return;
    saveUserPreference(
      userPreferenceKey,
      DASHBOARD_VISIBLE_CARDS_SCOPE,
      visibleDashboardCardIds,
    ).catch((error) => {
      console.error("Failed to save dashboard card order", error);
    });
  }, [visibleDashboardCardIds, isHydrated, userPreferenceKey]);

  const savedSpansSnapshotRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return;
    const snapshot = JSON.stringify(dashboardCardSpans);
    if (savedSpansSnapshotRef.current === null) {
      savedSpansSnapshotRef.current = snapshot;
      return;
    }
    if (snapshot === savedSpansSnapshotRef.current) return;
    savedSpansSnapshotRef.current = snapshot;

    const storageKey = getDashboardStorageKey(
      DASHBOARD_CARD_SPANS_STORAGE_KEY,
      userPreferenceKey,
    );
    window.localStorage.setItem(storageKey, JSON.stringify(dashboardCardSpans));

    if (!userPreferenceKey) return;
    saveUserPreference(
      userPreferenceKey,
      DASHBOARD_CARD_SPANS_SCOPE,
      dashboardCardSpans,
    ).catch((error) => {
      console.error("Failed to save dashboard card spans", error);
    });
  }, [dashboardCardSpans, isHydrated, userPreferenceKey]);

  const setDashboardCardVisibility = (cardId: DashboardCardId, visible: boolean) => {
    setVisibleDashboardCardIds((current) => {
      const exists = current.includes(cardId);

      if (visible && !exists) {
        return [...current, cardId];
      }

      if (!visible && exists) {
        return current.filter((id) => id !== cardId);
      }

      return current;
    });
  };

  const setDashboardCardSpan = (cardId: DashboardCardId, span: DashboardCardSpan) => {
    setDashboardCardSpans((current) => ({
      ...current,
      [cardId]: span,
    }));
  };

  const moveDashboardCard = (activeCardId: DashboardCardId, overCardId: DashboardCardId) => {
    setVisibleDashboardCardIds((current) => {
      const activeIndex = current.indexOf(activeCardId);
      const overIndex = current.indexOf(overCardId);

      if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(activeIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
  };

  const resetDashboardCards = () => {
    setVisibleDashboardCardIds(defaultVisibleDashboardCardIds);
    setDashboardCardSpans(defaultDashboardCardSpans);
  };

  return (
    <AppSettingsContext.Provider
      value={{
        visibleDashboardCardIds,
        dashboardCardSpans,
        setDashboardCardVisibility,
        setDashboardCardSpan,
        moveDashboardCard,
        resetDashboardCards,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error("useAppSettings must be used within an AppSettingsProvider");
  }
  return context;
};
